import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGridPro,
  GridPreferencePanelsValue,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import * as XLSX from "xlsx";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageContentCardSx,
  pageHeroShellSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import CpEntityChat, {
  getCpChatMetaKey,
} from "../CriticalPathHorizontal/CpEntityChat";
import CpChatDot from "../CriticalPathHorizontal/CpChatDot";
import NotasBadge from "../CriticalPathHorizontal/NotasBadge";
import {
  getNmlSuzhouCriticalPath,
  importNmlSuzhouRows,
  saveNmlSuzhouField,
  saveNmlSuzhouTask,
  setNmlSuzhouClosed,
} from "../../api/NmlSuzhou/nmlSuzhouApi";
import { getEntityChatSummary } from "../../api/Chat/entityChatApi";
import { getNotasCountAll } from "../../api/CriticalPath/criticalPathNotasApi";
import { getCpNotasCountKeyFromApiRow } from "../../utils/cpNotasEntityKey";
import { jwtDecode } from "jwt-decode";
import { getUserById } from "../../api/User/userApi";
import { mapUserToPermissionState } from "../../utils/userPermissionsUtils";
import {
  formatNmlDateLabel,
  nmlBoardFromSlug,
  nmlSuzhouDateInputValue,
  parseNmlDatePaste,
  canToggleNmlClientView,
} from "../../utils/nmlSuzhouCatalog";
import {
  BOARD_ACCENT,
  SECTION_ACCENT,
  NML_PINNED_DATA_FIELDS,
  buildNmlColumnGroupingModel,
  extractNmlStatusTokens,
  filterNmlSuzhouRows,
  formatNmlNumber,
  isNmlTaskLate,
  nmlCellClassName,
  nmlCellKind,
  nmlColorSwatch,
  nmlColumnWidth,
  nmlFirstLine,
  nmlHeaderClassName,
  nmlGroupI18nKey,
  nmlHeaderI18nKey,
  nmlInitials,
  nmlPinnedLeftFields,
  nmlColumnVisibilityStorageKey,
  readNmlColumnVisibility,
  writeNmlColumnVisibility,
  mergeNmlColumnVisibility,
  countNmlHiddenColumns,
  nmlRowIsLate,
  nmlShowsNotesAndChat,
  nmlTransportKind,
  nmlTaskCellValue,
  nmlSortValue,
  nmlDateSortComparator,
  nmlNumberSortComparator,
  summarizeNmlBoard,
  withSectionStarts,
} from "./nmlSuzhouGrid";
import NmlProductPhoto from "./NmlProductPhoto";

/** Fallback cuando el navegador bloquea clipboard (Ctrl+C/V entre celdas del mismo grid). */
let nmlDateClipboardMemory = "";

function chipToneSx(theme, tone, { mono = false, empty = false } = {}) {
  const dark = theme.palette.mode === "dark";
  const map = {
    po: {
      color: dark ? "#e2e8f0" : "#0f172a",
      bg: alpha(dark ? "#e2e8f0" : "#0f172a", dark ? 0.12 : 0.07),
    },
    nml: {
      color: dark ? "#5eead4" : "#0f766e",
      bg: alpha("#0f766e", dark ? 0.22 : 0.12),
    },
    expected: {
      color: dark ? "#93c5fd" : "#1d4ed8",
      bg: alpha("#2563eb", dark ? 0.22 : 0.1),
    },
    real: {
      color: dark ? "#6ee7b7" : "#047857",
      bg: alpha("#059669", dark ? 0.22 : 0.12),
    },
    late: { color: "#fff", bg: theme.palette.error.main },
    success: {
      color: dark ? "#6ee7b7" : "#047857",
      bg: alpha("#059669", dark ? 0.22 : 0.14),
    },
    warning: {
      color: dark ? "#fcd34d" : "#b45309",
      bg: alpha("#d97706", dark ? 0.22 : 0.16),
    },
    error: {
      color: dark ? "#fca5a5" : "#b91c1c",
      bg: alpha("#ef4444", dark ? 0.22 : 0.14),
    },
    info: {
      color: dark ? "#93c5fd" : "#1d4ed8",
      bg: alpha("#2563eb", dark ? 0.22 : 0.1),
    },
    sea: {
      color: dark ? "#7dd3fc" : "#0369a1",
      bg: alpha("#0284c7", dark ? 0.22 : 0.14),
    },
    air: {
      color: dark ? "#c4b5fd" : "#6d28d9",
      bg: alpha("#7c3aed", dark ? 0.22 : 0.14),
    },
    truck: {
      color: dark ? "#fcd34d" : "#a16207",
      bg: alpha("#ca8a04", dark ? 0.22 : 0.16),
    },
    factory: {
      color: dark ? "#99f6e4" : "#0f766e",
      bg: alpha("#0f766e", dark ? 0.22 : 0.12),
    },
    qty: {
      color: dark ? "#e2e8f0" : "#0f172a",
      bg: alpha("#0f172a", dark ? 0.16 : 0.05),
    },
    ghost: {
      color: theme.palette.text.disabled,
      bg: "transparent",
    },
  };
  const t = map[tone] || map.po;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.45,
    maxWidth: "100%",
    px: empty ? 0.7 : 0.85,
    py: 0.22,
    minHeight: 22,
    borderRadius: 1.75,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: 0.12,
    bgcolor: t.bg,
    color: t.color,
    border: "1px solid",
    borderColor: empty
      ? alpha(theme.palette.text.disabled, 0.32)
      : alpha(t.color, tone === "late" ? 0 : 0.22),
    borderStyle: empty ? "dashed" : "solid",
    fontFamily: mono
      ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace'
      : "inherit",
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    whiteSpace: "nowrap",
  };
}

function NmlSoftChip({
  label,
  tone = "po",
  icon,
  swatch,
  mono = false,
  empty = false,
  title,
}) {
  const theme = useTheme();
  return (
    <Box sx={chipToneSx(theme, tone, { mono, empty })} title={title || label}>
      {swatch ? (
        <Box
          sx={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            bgcolor: swatch,
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.18)",
            flexShrink: 0,
          }}
        />
      ) : null}
      {icon}
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

function transportIcon(kind) {
  const sx = { fontSize: 13 };
  if (kind === "air") return <FlightTakeoffOutlinedIcon sx={sx} />;
  if (kind === "sea") return <DirectionsBoatOutlinedIcon sx={sx} />;
  if (kind === "truck") return <LocalShippingOutlinedIcon sx={sx} />;
  return null;
}

function NmlGridCell({
  value,
  isDate,
  canEdit,
  onSave,
  kind,
  fieldKey,
  placeholder,
  slot,
  late,
  row,
  accent,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const iso = isDate ? nmlSuzhouDateInputValue(value) : "";
  const shown = isDate
    ? formatNmlDateLabel(value)
    : kind === "number"
      ? formatNmlNumber(value)
      : value == null
        ? ""
        : String(value);
  const cellKind = nmlCellKind(fieldKey);
  const swatch = cellKind === "color" ? nmlColorSwatch(value) : null;

  const startEdit = () => {
    setDraft(isDate ? iso : value == null ? "" : String(value));
    setEditing(true);
  };

  const copyDateToClipboard = async (rawIso) => {
    const isoVal = nmlSuzhouDateInputValue(rawIso);
    if (!isoVal) return;
    nmlDateClipboardMemory = isoVal;
    try {
      await navigator.clipboard.writeText(isoVal);
    } catch {
      /* memory fallback */
    }
  };

  const readDateFromClipboard = async () => {
    let raw = nmlDateClipboardMemory;
    try {
      const clip = await navigator.clipboard.readText();
      if (clip != null && String(clip).trim() !== "") raw = clip;
    } catch {
      /* memory fallback */
    }
    return parseNmlDatePaste(raw);
  };

  const handleDateHotkeys = async (e) => {
    if (!isDate) return false;
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return false;
    const key = String(e.key || "").toLowerCase();
    if (key === "c") {
      e.preventDefault();
      e.stopPropagation();
      await copyDateToClipboard(editing ? draft : value);
      return true;
    }
    if (key === "v" && canEdit) {
      e.preventDefault();
      e.stopPropagation();
      const parsed = await readDateFromClipboard();
      if (parsed === null) return true;
      if (editing) {
        setDraft(parsed);
        return true;
      }
      const current = nmlSuzhouDateInputValue(value);
      if (parsed === current) return true;
      await onSave(parsed);
      return true;
    }
    return false;
  };

  const body = (() => {
    if (isDate && !editing) {
      if (!shown) {
        return <NmlSoftChip empty tone="ghost" label="—" />;
      }
      return (
        <NmlSoftChip
          label={shown}
          tone={late ? "late" : slot === "real" ? "real" : "expected"}
        />
      );
    }
    if (!shown) {
      return (
        <Typography
          variant="body2"
          sx={{ fontSize: 12, color: "text.disabled" }}
        >
          —
        </Typography>
      );
    }
    if (cellKind === "code") {
      return (
        <NmlSoftChip
          mono
          label={shown}
          tone={fieldKey === "nmlNo" ? "nml" : "po"}
        />
      );
    }
    if (cellKind === "style") {
      return (
        <Stack spacing={0.15} minWidth={0}>
          <Typography
            noWrap
            title={shown}
            sx={{
              fontWeight: 800,
              fontSize: 12.5,
              letterSpacing: -0.25,
              lineHeight: 1.15,
            }}
          >
            {shown}
          </Typography>
          {row?.nmlNo ? (
            <Typography
              noWrap
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.2,
                color: "text.secondary",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              }}
            >
              {row.nmlNo}
            </Typography>
          ) : null}
        </Stack>
      );
    }
    if (cellKind === "person") {
      return (
        <Stack direction="row" spacing={0.7} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 8,
              fontWeight: 800,
              letterSpacing: 0.2,
              color: accent,
              bgcolor: alpha(accent, 0.16),
            }}
          >
            {nmlInitials(shown)}
          </Box>
          <Typography
            noWrap
            title={shown}
            sx={{ fontSize: 12, fontWeight: 700 }}
          >
            {shown}
          </Typography>
        </Stack>
      );
    }
    if (cellKind === "factory") {
      return (
        <NmlSoftChip
          tone="factory"
          label={shown}
          icon={<FactoryOutlinedIcon sx={{ fontSize: 13 }} />}
        />
      );
    }
    if (cellKind === "transport") {
      const transport = nmlTransportKind(shown) || "other";
      return (
        <NmlSoftChip
          label={shown}
          tone={transport === "other" ? "po" : transport}
          icon={transportIcon(transport)}
        />
      );
    }
    if (cellKind === "color") {
      return (
        <NmlSoftChip
          label={shown}
          tone="po"
          swatch={swatch || alpha(theme.palette.text.primary, 0.2)}
        />
      );
    }
    if (cellKind === "qty") {
      return <NmlSoftChip mono tone="qty" label={shown} />;
    }
    if (cellKind === "status") {
      const tokens = extractNmlStatusTokens(shown);
      const first = nmlFirstLine(shown);
      return (
        <Stack spacing={0.35} minWidth={0} width="100%">
          {tokens.length > 0 ? (
            <Stack direction="row" spacing={0.4} sx={{ minWidth: 0 }}>
              {tokens.slice(0, 2).map((token) => (
                <NmlSoftChip
                  key={token.token}
                  label={t(`NmlSuzhou.tokens.${token.token}`)}
                  tone={token.tone}
                />
              ))}
            </Stack>
          ) : null}
          <Typography
            noWrap
            title={shown}
            sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}
          >
            {first}
          </Typography>
        </Stack>
      );
    }
    if (cellKind === "note") {
      return (
        <Typography
          title={shown}
          sx={{
            fontSize: 11,
            color: "text.secondary",
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {shown}
        </Typography>
      );
    }
    return (
      <Typography
        variant="body2"
        noWrap
        title={shown}
        sx={{
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
          color: "text.primary",
        }}
      >
        {shown}
      </Typography>
    );
  })();

  if (!canEdit) {
    if (isDate) {
      return (
        <Box
          tabIndex={0}
          onKeyDown={(e) => {
            void handleDateHotkeys(e);
          }}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            px: 0.5,
            outline: "none",
            "&:focus-visible": {
              boxShadow: `inset 0 0 0 1px ${alpha(accent || theme.palette.primary.main, 0.45)}`,
            },
          }}
          title={t("NmlSuzhou.dateCopyHint", {
            defaultValue: "Ctrl+C to copy date",
          })}
        >
          {body}
        </Box>
      );
    }
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          px: 0.5,
        }}
      >
        {body}
      </Box>
    );
  }

  if (!editing) {
    const tip = isDate
      ? t("NmlSuzhou.dateCopyPasteHint", {
          defaultValue:
            "Click = select · Ctrl+C / Ctrl+V · double-click or Enter = edit",
        })
      : placeholder;
    return (
      <Box
        tabIndex={0}
        onMouseDown={(e) => {
          // Focus before click so Ctrl+C works; don't let the grid steal the event.
          if (!isDate) return;
          e.stopPropagation();
          e.currentTarget.focus();
        }}
        onClick={(e) => {
          if (isDate) {
            e.stopPropagation();
            e.currentTarget.focus();
            return;
          }
          startEdit();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          startEdit();
        }}
        onKeyDown={(e) => {
          void (async () => {
            if (await handleDateHotkeys(e)) return;
            if (e.ctrlKey || e.metaKey) return;
            if (e.key === "Enter" || e.key === "F2") {
              e.preventDefault();
              startEdit();
            }
          })();
        }}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          cursor: isDate ? "cell" : "text",
          px: 0.5,
          borderRadius: 0.5,
          outline: "none",
          "&:hover": { bgcolor: "action.hover" },
          "&:focus": {
            boxShadow: `inset 0 0 0 1.5px ${alpha(accent || theme.palette.primary.main, 0.55)}`,
          },
          "&:focus-visible": {
            boxShadow: `inset 0 0 0 1.5px ${alpha(accent || theme.palette.primary.main, 0.55)}`,
          },
        }}
        title={tip}
      >
        {body}
      </Box>
    );
  }

  return (
    <TextField
      autoFocus
      type={isDate ? "date" : kind === "number" ? "number" : "text"}
      size="small"
      variant="standard"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onBlur={async () => {
        setEditing(false);
        const current = isDate ? iso : value == null ? "" : String(value);
        if (draft === current) return;
        await onSave(draft);
      }}
      onPaste={(e) => {
        if (!isDate) return;
        const text = e.clipboardData?.getData("text");
        const parsed = parseNmlDatePaste(text);
        if (parsed === null) return;
        e.preventDefault();
        e.stopPropagation();
        setDraft(parsed);
        if (parsed) nmlDateClipboardMemory = parsed;
      }}
      onKeyDown={(e) => {
        // DataGridPro treats Space / arrows as cell navigation; keep them in the input.
        e.stopPropagation();
        void handleDateHotkeys(e);
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setEditing(false);
        }
      }}
      onKeyUp={(e) => e.stopPropagation()}
      InputLabelProps={{ shrink: true }}
      sx={{ width: "100%", "& input": { fontSize: 12, py: 0.25 } }}
    />
  );
}

function LegendChip({ color, label }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        height: 26,
        px: 1,
        borderRadius: 999,
        bgcolor: alpha(color, 0.12),
        border: "1px solid",
        borderColor: alpha(color, 0.22),
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: 99,
          bgcolor: color,
          boxShadow: `0 0 0 3px ${alpha(color, 0.18)}`,
        }}
      />
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, letterSpacing: 0.2, color, lineHeight: 1 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function KpiChip({ label, value, color }) {
  return (
    <Chip
      size="small"
      label={
        <Box component="span" sx={{ display: "inline-flex", gap: 0.6 }}>
          <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {value}
          </Box>
          <Box component="span" sx={{ opacity: 0.78, fontWeight: 700 }}>
            {label}
          </Box>
        </Box>
      }
      sx={{
        height: 28,
        px: 0.4,
        fontWeight: 800,
        bgcolor: alpha(color, 0.12),
        color,
        border: "1px solid",
        borderColor: alpha(color, 0.22),
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

export default function NmlSuzhouCriticalPath() {
  const { t } = useTranslation();
  const { board } = useParams();
  const boardMeta = nmlBoardFromSlug(board) || nmlBoardFromSlug("suzhou");
  const boardKey = boardMeta.key;
  const theme = useTheme();
  const { contentMargin } = useContext(SidebarContext);
  const apiRef = useGridApiRef();
  const [payload, setPayload] = useState({
    columns: [],
    rows: [],
    clientView: false,
    canEdit: false,
    canToggleClientView: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeClosed, setIncludeClosed] = useState(false);
  const [previewClient, setPreviewClient] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState({ open: false, src: "", title: "" });
  const [chat, setChat] = useState({
    open: false,
    productId: null,
    title: "",
    initialTab: "notas",
  });
  const [notasCountByProduct, setNotasCountByProduct] = useState({});
  const [chatMetaMap, setChatMetaMap] = useState({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const userId = useMemo(() => {
    try {
      return jwtDecode(localStorage.getItem("token") || "").userId ?? "anon";
    } catch {
      return "anon";
    }
  }, []);

  const columnVisibilityKey = useMemo(
    () =>
      nmlColumnVisibilityStorageKey({
        userId,
        boardKey,
        clientView: payload.clientView,
      }),
    [userId, boardKey, payload.clientView],
  );

  const pinnedLeft = useMemo(
    () =>
      nmlPinnedLeftFields({
        clientView: payload.clientView,
        canEdit: payload.canEdit,
      }),
    [payload.clientView, payload.canEdit],
  );

  useEffect(() => {
    setColumnVisibilityModel(
      mergeNmlColumnVisibility(
        readNmlColumnVisibility(columnVisibilityKey),
        pinnedLeft,
      ),
    );
  }, [columnVisibilityKey, pinnedLeft]);

  const handleColumnVisibilityModelChange = useCallback(
    (model) => {
      const next = mergeNmlColumnVisibility(model, pinnedLeft);
      setColumnVisibilityModel(next);
      writeNmlColumnVisibility(columnVisibilityKey, next);
    },
    [columnVisibilityKey, pinnedLeft],
  );

  const resetColumnVisibility = useCallback(() => {
    setColumnVisibilityModel(mergeNmlColumnVisibility({}, pinnedLeft));
    writeNmlColumnVisibility(columnVisibilityKey, {});
  }, [columnVisibilityKey, pinnedLeft]);

  const openColumnsPanel = useCallback(() => {
    apiRef.current?.showPreferences?.(GridPreferencePanelsValue.columns);
  }, [apiRef]);

  const patchRow = useCallback((productId, patchFn) => {
    setPayload((prev) => ({
      ...prev,
      rows: prev.rows.map((row) =>
        row.productId === productId ? patchFn(row) : row,
      ),
    }));
  }, []);

  const loadBadges = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const { userId } = jwtDecode(token);
      const [notasCountData, prodSummary] = await Promise.all([
        getNotasCountAll(),
        getEntityChatSummary("Producto", userId),
      ]);
      const notasMap = {};
      (Array.isArray(notasCountData) ? notasCountData : []).forEach((row) => {
        const key = getCpNotasCountKeyFromApiRow(row);
        if (!key || !key.startsWith("P_")) return;
        notasMap[Number(key.slice(2))] = Number(row.total) || 0;
      });
      setNotasCountByProduct(notasMap);
      const chatMap = {};
      Object.entries(prodSummary || {}).forEach(([id, meta]) => {
        chatMap[`Producto:${Number(id)}`] = {
          total: Number(meta.total) || 0,
          noLeidos: Number(meta.noLeidos) || 0,
          hasMessages: (Number(meta.total) || 0) > 0,
          hasMention: false,
        };
      });
      setChatMetaMap(chatMap);
    } catch (err) {
      console.error("NML badges:", err);
    }
  }, []);

  const refreshNotasForProduct = useCallback(async (productId) => {
    if (!productId) return;
    try {
      const data = await getNotasCountAll();
      const row = (Array.isArray(data) ? data : []).find(
        (r) => Number(r.ProductId) === Number(productId),
      );
      setNotasCountByProduct((prev) => ({
        ...prev,
        [Number(productId)]: Number(row?.total) || 0,
      }));
    } catch (err) {
      console.error("NML refresh notas:", err);
    }
  }, []);

  const getRowChatMeta = useCallback(
    (productId) => {
      const key = getCpChatMetaKey({ productId });
      const meta = (key && chatMetaMap[key]) || {};
      return {
        total: Number(meta.total) || 0,
        noLeidos: Number(meta.noLeidos) || 0,
        hasMention: Boolean(meta.hasMention),
        hasMessages: Boolean(meta.hasMessages) || (Number(meta.total) || 0) > 0,
      };
    },
    [chatMetaMap],
  );

  const openNmlChat = useCallback((row, tab = "notas") => {
    if (!row?.productId) return;
    const metaKey = getCpChatMetaKey({ productId: row.productId });
    setChat({
      open: true,
      productId: row.productId,
      title: row.styleReference || row.productName || String(row.productId),
      initialTab: tab,
    });
    if (metaKey) {
      setChatMetaMap((prev) => {
        const cur = prev[metaKey];
        if (!cur) return prev;
        return {
          ...prev,
          [metaKey]: { ...cur, noLeidos: 0, hasMention: false },
        };
      });
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getNmlSuzhouCriticalPath({
        includeClosed,
        board: boardKey,
        clientView: previewClient,
      });
      setPayload({
        columns: Array.isArray(data?.columns) ? data.columns : [],
        rows: Array.isArray(data?.rows) ? data.rows : [],
        clientView: Boolean(data?.clientView),
        canEdit: Boolean(data?.canEdit),
        canToggleClientView: Boolean(data?.canToggleClientView),
      });
      if (!data?.clientView) {
        void loadBadges();
      } else {
        setNotasCountByProduct({});
        setChatMetaMap({});
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("NmlSuzhou.loadError"),
      );
    } finally {
      setLoading(false);
    }
  }, [includeClosed, boardKey, previewClient, t, loadBadges]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const decoded = jwtDecode(token);
        const id = decoded.userId ?? decoded.id;
        if (id == null) return;
        const user = await getUserById(id);
        if (!cancelled) setUserPermissions(mapUserToPermissionState(user));
      } catch {
        /* el chip puede salir igual por canToggleClientView del payload */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const showClientToggle =
    canToggleNmlClientView(userPermissions) || payload.canToggleClientView;

  const handleFieldSave = useCallback(
    async (productId, field, value) => {
      const next = value || null;
      patchRow(productId, (row) => ({ ...row, [field]: next }));
      await saveNmlSuzhouField({ productId, field, value: next });
    },
    [patchRow],
  );

  const handleTaskSave = useCallback(
    async (productId, col, value) => {
      const date = value || null;
      const slot = col.slot || "expected";
      patchRow(productId, (row) => {
        const prevTask = row.tasks?.[col.taskKey] || {};
        return {
          ...row,
          tasks: {
            ...(row.tasks || {}),
            [col.taskKey]: { ...prevTask, [slot]: date },
          },
        };
      });
      await saveNmlSuzhouTask({
        productId,
        taskKey: col.taskKey,
        expectedDate: col.slot === "real" ? undefined : date,
        realDate: col.slot === "expected" ? undefined : date,
        slot: col.slot || (col.pair ? "expected" : undefined),
      });
    },
    [patchRow],
  );

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setError("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: null,
        raw: true,
      });
      const body = rows
        .slice(3)
        .filter((r) =>
          Array.isArray(r) ? r.some((c, i) => i >= 1 && i <= 5 && c) : false,
        );
      const result = await importNmlSuzhouRows(body, boardKey);
      await load();
      if (result?.unmatched) {
        setError(
          t("NmlSuzhou.importPartial", {
            updated: result.updated,
            unmatched: result.unmatched,
          }),
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("NmlSuzhou.importError"),
      );
    } finally {
      setImporting(false);
    }
  };

  const accent = BOARD_ACCENT[boardKey] || BOARD_ACCENT.SUZHOU;
  const expectedBlue = theme.palette.mode === "dark" ? "#93c5fd" : "#2563eb";
  const realGreen = theme.palette.mode === "dark" ? "#6ee7b7" : "#059669";
  const boardStats = useMemo(
    () => summarizeNmlBoard(payload.rows),
    [payload.rows],
  );

  const columnGroupingModel = useMemo(
    () =>
      buildNmlColumnGroupingModel(payload.columns).map((group) => {
        const section = String(group.headerClassName || "").match(
          /nml-section-(\w+)/,
        )?.[1];
        const color = SECTION_ACCENT[section] || accent;
        const groupKey = nmlGroupI18nKey(group.groupId);
        const groupTitle = groupKey ? t(groupKey) : group.headerName;
        return {
          ...group,
          headerName: groupTitle,
          renderHeaderGroup: () => (
            <Tooltip title={groupTitle} enterDelay={500}>
              <Stack
                direction="row"
                spacing={0.6}
                alignItems="center"
                justifyContent="center"
                sx={{ width: "100%", px: 0.4 }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: 99,
                    bgcolor: color,
                    boxShadow: `0 0 0 3px ${alpha(color, 0.18)}`,
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: 0.15,
                    textAlign: "center",
                    whiteSpace: "normal",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {groupTitle}
                </Box>
              </Stack>
            </Tooltip>
          ),
        };
      }),
    [payload.columns, accent, t],
  );

  const visibleRows = useMemo(
    () => filterNmlSuzhouRows(payload.rows, query),
    [payload.rows, query],
  );

  const columns = useMemo(() => {
    const actionCols = [
      {
        field: "look",
        headerName: t("NmlSuzhou.photo"),
        width: 80,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        renderHeader: () => null,
        renderCell: (params) => (
          <NmlProductPhoto
            productId={params.row.productId}
            alt={params.row.styleReference || params.row.productName}
            onOpen={(src, title) => setPreview({ open: true, src, title })}
          />
        ),
      },
    ];
    if (nmlShowsNotesAndChat(payload.clientView)) {
      actionCols.push(
        {
          field: "chat",
          headerName: "",
          width: 40,
          sortable: false,
          filterable: false,
          hideable: false,
          disableColumnMenu: true,
          renderHeader: () => null,
          renderCell: (params) => {
            const { noLeidos, hasMessages, hasMention } = getRowChatMeta(
              params.row.productId,
            );
            return (
              <CpChatDot
                noLeidos={noLeidos}
                hasMessages={hasMessages}
                hasMention={hasMention}
                size={28}
                onClick={() => openNmlChat(params.row, "mensajes")}
              />
            );
          },
        },
        {
          field: "notes",
          headerName: "",
          width: 40,
          sortable: false,
          filterable: false,
          hideable: false,
          disableColumnMenu: true,
          renderHeader: () => null,
          renderCell: (params) => (
            <Box
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <NotasBadge
                key={`${params.row.productId}-${
                  notasCountByProduct[params.row.productId] || 0
                }`}
                productId={params.row.productId}
                notasCount={notasCountByProduct[params.row.productId] || 0}
                onOpenDialog={() => openNmlChat(params.row, "notas")}
              />
            </Box>
          ),
        },
      );
    }
    if (payload.canEdit) {
      actionCols.push({
        field: "cerrar",
        headerName: "",
        width: 48,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        renderHeader: () => null,
        renderCell: (params) => (
          <Tooltip
            title={
              params.row.isClosed ? t("NmlSuzhou.reopen") : t("NmlSuzhou.close")
            }
          >
            <IconButton
              size="small"
              onClick={async (e) => {
                e.stopPropagation();
                const isClosed = !params.row.isClosed;
                patchRow(params.row.productId, (row) => ({ ...row, isClosed }));
                await setNmlSuzhouClosed({
                  productId: params.row.productId,
                  isClosed,
                });
              }}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: params.row.isClosed
                  ? alpha(theme.palette.text.primary, 0.08)
                  : alpha(accent, 0.08),
                color: params.row.isClosed ? "text.secondary" : "text.primary",
                "&:hover": {
                  bgcolor: params.row.isClosed
                    ? alpha(theme.palette.text.primary, 0.14)
                    : alpha(accent, 0.16),
                },
              }}
            >
              {params.row.isClosed ? (
                <LockOutlinedIcon sx={{ fontSize: 16 }} />
              ) : (
                <LockOpenOutlinedIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        ),
      });
    }

    const dataCols = withSectionStarts(payload.columns || []).map((col) => {
      const isDate = col.kind === "date";
      const width = nmlColumnWidth(col);
      const titleKey = nmlHeaderI18nKey(col);
      const groupKey = nmlGroupI18nKey(col.groupId);
      const colTitle = titleKey ? t(titleKey) : col.label;
      const headerTip = groupKey
        ? t(groupKey)
        : titleKey
          ? t(titleKey)
          : col.fullLabel || col.groupLabel || col.label;
      return {
        field: col.key,
        headerName: colTitle,
        width,
        minWidth: col.slot ? 108 : 64,
        sortable: true,
        sortingOrder: ["asc", "desc", null],
        hideable: !NML_PINNED_DATA_FIELDS.includes(col.key),
        filterable: true,
        type: col.kind === "number" ? "number" : "string",
        valueGetter: (params) => nmlSortValue(params.row, col),
        sortComparator: isDate
          ? nmlDateSortComparator
          : col.kind === "number"
            ? nmlNumberSortComparator
            : undefined,
        headerAlign:
          col.slot || col.kind === "number" || col.kind === "date"
            ? "center"
            : "left",
        align:
          col.kind === "number" || col.groupId === "sizeBreakDown" || isDate
            ? "center"
            : "left",
        headerClassName: nmlHeaderClassName(col),
        cellClassName: (params) => nmlCellClassName(col, params.row),
        renderHeader: () => {
          if (col.slot === "expected" || col.slot === "real") {
            const isReal = col.slot === "real";
            const color = isReal ? realGreen : expectedBlue;
            return (
              <Tooltip title={headerTip} enterDelay={400}>
                <Box
                  sx={{
                    mx: "auto",
                    px: 0.75,
                    py: 0.15,
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: 0.45,
                    textTransform: "uppercase",
                    bgcolor: alpha(color, 0.16),
                    color,
                  }}
                >
                  {isReal
                    ? t("NmlSuzhou.slotReal")
                    : t("NmlSuzhou.slotExpected")}
                </Box>
              </Tooltip>
            );
          }
          return (
            <Tooltip title={headerTip} enterDelay={400}>
              <Box
                sx={{
                  width: "100%",
                  fontSize: 10.5,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  letterSpacing: 0.15,
                  textAlign: "center",
                  whiteSpace: "normal",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {colTitle}
              </Box>
            </Tooltip>
          );
        },
        renderCell: (params) => {
          const value = isDate
            ? nmlTaskCellValue(params.row, col)
            : params.row[col.key];
          return (
            <NmlGridCell
              value={value}
              isDate={isDate}
              kind={col.kind}
              fieldKey={col.key}
              slot={col.slot}
              late={isNmlTaskLate(params.row, col)}
              row={params.row}
              accent={accent}
              canEdit={Boolean(payload.canEdit && col.editable)}
              placeholder={t("NmlSuzhou.clickToEdit")}
              onSave={(next) =>
                isDate
                  ? handleTaskSave(params.row.productId, col, next)
                  : handleFieldSave(params.row.productId, col.key, next)
              }
            />
          );
        },
      };
    });

    return [...actionCols, ...dataCols];
  }, [
    payload.canEdit,
    payload.clientView,
    payload.columns,
    patchRow,
    handleFieldSave,
    handleTaskSave,
    getRowChatMeta,
    openNmlChat,
    notasCountByProduct,
    t,
    accent,
    expectedBlue,
    realGreen,
    theme,
  ]);

  const hiddenColumnsCount = useMemo(
    () => countNmlHiddenColumns(columnVisibilityModel, columns),
    [columnVisibilityModel, columns],
  );

  const pinnedColumns = useMemo(() => ({ left: pinnedLeft }), [pinnedLeft]);

  return (
    <Box
      sx={{
        ...contentMargin,
        p: { xs: 1.25, md: 2 },
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        background:
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : `radial-gradient(1200px 420px at 0% -10%, ${alpha(accent, 0.16)}, transparent 55%),
               linear-gradient(180deg, #f6f8fb 0%, #eef2f6 100%)`,
      }}
    >
      <Box
        sx={{
          ...pageHeroShellSx(theme, { sticky: false }),
          background:
            theme.palette.mode === "dark"
              ? pageHeroShellSx(theme).background
              : `linear-gradient(120deg, ${alpha(accent, 0.14)} 0%, #ffffff 42%, ${alpha("#6366f1", 0.08)} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ px: 2.25, py: 1.35 }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            minWidth={0}
            flexWrap="wrap"
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background: `linear-gradient(145deg, ${accent}, ${alpha(accent, 0.72)})`,
                boxShadow: `0 10px 24px ${alpha(accent, 0.35)}`,
              }}
            >
              <ViewWeekOutlinedIcon fontSize="small" />
            </Box>
            <Box minWidth={0}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.08rem", md: "1.22rem" },
                  letterSpacing: -0.2,
                  lineHeight: 1.15,
                }}
              >
                {t(`NmlSuzhou.boards.${boardKey}`)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              <KpiChip
                color={accent}
                value={query.trim() ? visibleRows.length : boardStats.open}
                label={
                  query.trim()
                    ? t("NmlSuzhou.kpiMatch")
                    : t("NmlSuzhou.kpiOpen")
                }
              />
              {boardStats.late > 0 && (
                <KpiChip
                  color={theme.palette.error.main}
                  value={boardStats.late}
                  label={t("NmlSuzhou.kpiLate")}
                />
              )}
              {boardStats.closed > 0 && (
                <KpiChip
                  color={theme.palette.text.secondary}
                  value={boardStats.closed}
                  label={t("NmlSuzhou.kpiClosed")}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("NmlSuzhou.search")}
              sx={{
                minWidth: { sm: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.background.paper, 0.86),
                  fontSize: 13,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQuery("")}>
                      <ClearOutlinedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ px: 0.25 }}
            >
              <LegendChip
                color={expectedBlue}
                label={t("NmlSuzhou.legendExpected")}
              />
              <LegendChip color={realGreen} label={t("NmlSuzhou.legendReal")} />
            </Stack>
            <Tooltip
              title={
                hiddenColumnsCount > 0
                  ? t("NmlSuzhou.columnsHiddenHint", {
                      count: hiddenColumnsCount,
                      defaultValue: "{{count}} hidden columns",
                    })
                  : t("NmlSuzhou.columnsButton", {
                      defaultValue: "Show / hide columns",
                    })
              }
            >
              <Badge
                color="warning"
                badgeContent={hiddenColumnsCount}
                invisible={hiddenColumnsCount === 0}
                overlap="rectangular"
                sx={{
                  "& .MuiBadge-badge": {
                    fontWeight: 800,
                    fontSize: 10,
                    minWidth: 18,
                    height: 18,
                  },
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ViewColumnOutlinedIcon />}
                  onClick={openColumnsPanel}
                  sx={{
                    height: 32,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 800,
                    px: 1.25,
                    ...(hiddenColumnsCount > 0
                      ? {
                          borderColor: alpha(theme.palette.warning.main, 0.55),
                          color: theme.palette.warning.dark,
                        }
                      : null),
                  }}
                >
                  {t("NmlSuzhou.columnsButton", { defaultValue: "Columns" })}
                </Button>
              </Badge>
            </Tooltip>
            <Tooltip
              title={t("NmlSuzhou.columnsReset", {
                defaultValue: "Reset columns",
              })}
            >
              <IconButton size="small" onClick={resetColumnVisibility}>
                <RestartAltOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {showClientToggle && (
              <Tooltip
                title={
                  previewClient
                    ? t("NmlSuzhou.factoryViewToggleHint")
                    : t("NmlSuzhou.clientViewToggleHint")
                }
              >
                <Chip
                  clickable
                  size="small"
                  color={previewClient ? "success" : "info"}
                  variant="filled"
                  label={
                    previewClient
                      ? t("NmlSuzhou.factoryViewToggle")
                      : t("NmlSuzhou.clientViewToggle")
                  }
                  onClick={() => setPreviewClient((v) => !v)}
                  sx={{
                    height: 32,
                    borderRadius: 999,
                    fontWeight: 800,
                    px: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                  }}
                />
              </Tooltip>
            )}
            <Chip
              clickable
              size="small"
              variant={includeClosed ? "filled" : "outlined"}
              label={t("NmlSuzhou.includeClosed")}
              onClick={() => setIncludeClosed((v) => !v)}
              sx={{
                height: 32,
                borderRadius: 999,
                fontWeight: 800,
                px: 0.5,
              }}
            />
            {payload.canEdit && (
              <Button
                component="label"
                startIcon={<UploadFileOutlinedIcon />}
                sx={{
                  ...pagePrimaryButtonSx(theme),
                  py: 0.7,
                  px: 1.5,
                  fontSize: 13,
                }}
                disabled={importing}
              >
                {t("NmlSuzhou.importExcel")}
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    void handleImport(file);
                  }}
                />
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ my: 1 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          ...pageContentCardSx(theme, accent),
          p: 0,
          flex: 1,
          minHeight: 0,
          display: "flex",
          overflow: "hidden",
          borderRadius: 3.5,
        }}
      >
        {loading ? (
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress />
          </Stack>
        ) : (
          <DataGridPro
            apiRef={apiRef}
            rows={visibleRows}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            experimentalFeatures={{ columnGrouping: true }}
            pinnedColumns={pinnedColumns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            getRowId={(row) => row.productId}
            density="compact"
            disableRowSelectionOnClick
            onCellKeyDown={(params, event) => {
              const tag = String(event?.target?.tagName || "").toLowerCase();
              if (
                tag === "input" ||
                tag === "textarea" ||
                event?.target?.isContentEditable
              ) {
                event.stopPropagation();
              }
            }}
            columnHeaderHeight={42}
            columnGroupHeaderHeight={46}
            rowHeight={76}
            getRowClassName={(params) =>
              [
                params.row.isClosed ? "nml-suzhou-closed" : "",
                nmlRowIsLate(params.row) ? "nml-row-late" : "",
              ]
                .filter(Boolean)
                .join(" ")
            }
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 50 } },
            }}
            localeText={{
              noRowsLabel: query.trim()
                ? t("NmlSuzhou.emptySearch", { query: query.trim() })
                : t("NmlSuzhou.empty"),
            }}
            sx={{
              border: 0,
              flex: 1,
              fontFamily: "inherit",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.paper, 0.72)
                    : "rgba(255,255,255,0.78)",
                backdropFilter: "blur(16px)",
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                height: 10,
                width: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                borderRadius: 999,
                bgcolor: alpha(accent, 0.35),
              },
              "& .nml-suzhou-closed": {
                bgcolor: alpha(theme.palette.text.disabled, 0.04),
                opacity: 0.72,
              },
              "& .nml-row-late": {
                boxShadow: `inset 3px 0 0 ${theme.palette.error.main}`,
              },
              "& .MuiDataGrid-row": {
                transition: "background-color 0.12s ease",
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.32)}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 800,
                fontSize: 10.5,
                letterSpacing: 0.15,
                whiteSpace: "normal",
                lineHeight: 1.2,
                textTransform: "none",
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                padding: "0 4px",
              },
              "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                width: "100%",
                whiteSpace: "normal",
                justifyContent: "center",
              },
              "& .MuiDataGrid-columnGroupHeader": {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
              },
              "& .nml-section-order": {
                boxShadow: `inset 0 2px 0 ${SECTION_ACCENT.order}`,
              },
              "& .nml-section-line": {
                boxShadow: `inset 0 2px 0 ${SECTION_ACCENT.line}`,
              },
              "& .nml-section-calendar": {
                boxShadow: `inset 0 2px 0 ${SECTION_ACCENT.calendar}`,
              },
              "& .nml-section-fabric": {
                boxShadow: `inset 0 2px 0 ${SECTION_ACCENT.fabric}`,
              },
              "& .nml-section-production": {
                boxShadow: `inset 0 2px 0 ${SECTION_ACCENT.production}`,
              },
              "& .nml-section-sizes": {
                boxShadow: `inset 0 2px 0 ${SECTION_ACCENT.sizes}`,
              },
              "& .nml-section-start": {
                borderLeft: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              },
              "& .nml-c-expected": {
                bgcolor: alpha(expectedBlue, 0.035),
              },
              "& .nml-c-real": {
                bgcolor: alpha(realGreen, 0.035),
              },
              "& .nml-c-late": {
                bgcolor: alpha(theme.palette.error.main, 0.1),
              },
              "& .MuiDataGrid-cell": {
                borderRight: "none",
                py: 0,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: alpha(accent, 0.055),
              },
              "& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders":
                {
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.paper, 0.97)
                      : "rgba(255,255,255,0.94)",
                  backdropFilter: "blur(14px)",
                  boxShadow: `12px 0 28px ${alpha(theme.palette.common.black, 0.06)}`,
                },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                minHeight: 44,
              },
            }}
          />
        )}
      </Box>

      <Dialog
        open={preview.open}
        onClose={() => setPreview({ open: false, src: "", title: "" })}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#0b0f14",
          },
        }}
      >
        {preview.title && (
          <Typography sx={{ color: "#fff", px: 2, pt: 1.5, fontWeight: 700 }}>
            {preview.title}
          </Typography>
        )}
        {preview.src && (
          <Box
            component="img"
            src={preview.src}
            alt={preview.title}
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              display: "block",
            }}
          />
        )}
      </Dialog>
      {!payload.clientView && (
        <CpEntityChat
          open={chat.open}
          onClose={() => {
            const productId = chat.productId;
            setChat((s) => ({ ...s, open: false }));
            void refreshNotasForProduct(productId);
            void loadBadges();
          }}
          productId={chat.productId}
          title={chat.title}
          subtitle={t(`NmlSuzhou.boards.${boardKey}`)}
          initialTab={chat.initialTab}
          onNotasChanged={() => refreshNotasForProduct(chat.productId)}
        />
      )}
    </Box>
  );
}
